// AtlasBlocksSection — renders public Atlas blocks below a blog article.
//
// Fetches `/api/public/atlas-blocks/{entity_type}/{slug}` and displays blocks
// grouped by pedagogical category (Histoire, Terroir, Cépages, Accords…).
// Each category expands into a list of short "fiche" cards; markdown inside
// each block is rendered lightly (headings, bold, italic, lists).
// At the bottom, a CTA invites the reader to the full fiche on app.geovinum.io.
//
// The section auto-hides when the upstream returns zero public blocks so
// articles without an Atlas fiche render exactly like before.
import { useEffect, useMemo, useState } from "react";
import { ExternalLink, ChevronDown, Loader2 } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { ATLAS_CATEGORIES } from "@/lib/atlasCategories";

function slugify(v) {
  if (!v) return "";
  return String(v)
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")  // strip accents
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

// Light Markdown → HTML (mirrors BlogArticlePage's approach but simpler).
function renderMd(md) {
  if (!md) return "";
  let h = String(md);
  h = h.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  h = h.replace(/^### (.+)$/gm, "<h4>$1</h4>");
  h = h.replace(/^## (.+)$/gm, "<h3>$1</h3>");
  h = h.replace(/^# (.+)$/gm, "<h3>$1</h3>");
  h = h.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  h = h.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  h = h.replace(/^(?:- (.+)\n?)+/gm, (m) => {
    const items = m.trim().split("\n").map((l) => l.replace(/^- /, "").trim()).map((tx) => `<li>${tx}</li>`).join("");
    return `<ul>${items}</ul>`;
  });
  h = h.split(/\n{2,}/).map((p) => {
    const tx = p.trim();
    if (!tx) return "";
    if (/^<(h[3-6]|ul|ol|blockquote|pre)/.test(tx)) return tx;
    return `<p>${tx.replace(/\n/g, "<br/>")}</p>`;
  }).join("\n");
  return h;
}

// Localised labels for the section chrome. Category labels come from
// ATLAS_CATEGORIES (still FR-only in source-of-truth — we override per-lang here).
const COPY = {
  fr: { eyebrow: "Fiche complète · geoVINUM Atlas", title: (n) => `Tout savoir sur ${n}`, subtitle: (c) => `${c} sections publiques issues de la fiche pédagogique enrichie.`, cta: "Voir la fiche complète sur l\u2019app geoVINUM", translating: (d, t) => `Traduction en cours… ${d}/${t}` },
  en: { eyebrow: "Full fact-sheet · geoVINUM Atlas", title: (n) => `Everything about ${n}`, subtitle: (c) => `${c} public sections from the enriched fact-sheet.`, cta: "See the full fact-sheet on the geoVINUM app", translating: (d, t) => `Translating… ${d}/${t}` },
  es: { eyebrow: "Ficha completa · geoVINUM Atlas", title: (n) => `Todo sobre ${n}`, subtitle: (c) => `${c} secciones públicas de la ficha pedagógica enriquecida.`, cta: "Ver la ficha completa en la app geoVINUM", translating: (d, t) => `Traduciendo… ${d}/${t}` },
  pt: { eyebrow: "Ficha completa · geoVINUM Atlas", title: (n) => `Tudo sobre ${n}`, subtitle: (c) => `${c} secções públicas da ficha pedagógica enriquecida.`, cta: "Ver a ficha completa na app geoVINUM", translating: (d, t) => `A traduzir… ${d}/${t}` },
};

const CATEGORY_LABEL_I18N = {
  histoire:         { fr: "Histoire",             en: "History",            es: "Historia",         pt: "História" },
  geographie:       { fr: "Géographie",           en: "Geography",          es: "Geografía",        pt: "Geografia" },
  terroir_geologie: { fr: "Terroir & Géologie",   en: "Terroir & Geology",  es: "Terruño y Geología", pt: "Terroir e Geologia" },
  climat:           { fr: "Climat",               en: "Climate",            es: "Clima",            pt: "Clima" },
  cepages:          { fr: "Cépages",              en: "Grape varieties",    es: "Uvas",             pt: "Castas" },
  viticulture:      { fr: "Viticulture",          en: "Viticulture",        es: "Viticultura",      pt: "Viticultura" },
  vinification:     { fr: "Vinification",         en: "Winemaking",         es: "Vinificación",     pt: "Vinificação" },
  les_vins:         { fr: "Les Vins",             en: "The Wines",          es: "Los Vinos",        pt: "Os Vinhos" },
  climats_crus:     { fr: "Climats & Crus",       en: "Climats & Crus",     es: "Climats y Crus",   pt: "Climats e Crus" },
  accords:          { fr: "Accords Mets & Vins",  en: "Food & Wine Pairings", es: "Maridajes",      pt: "Harmonizações" },
  producteurs:      { fr: "Producteurs",          en: "Producers",          es: "Productores",      pt: "Produtores" },
  patrimoine:       { fr: "Patrimoine & Culture", en: "Heritage & Culture", es: "Patrimonio y Cultura", pt: "Património e Cultura" },
  anecdotes:        { fr: "Anecdotes & Secrets",  en: "Anecdotes & Secrets", es: "Anécdotas y Secretos", pt: "Anedotas e Segredos" },
  chiffres_cles:    { fr: "Chiffres Clés",        en: "Key Figures",        es: "Cifras Clave",     pt: "Números-chave" },
  oenotourisme:     { fr: "Œnotourisme",          en: "Wine Tourism",       es: "Enoturismo",       pt: "Enoturismo" },
  glossaire:        { fr: "Glossaire",            en: "Glossary",           es: "Glosario",         pt: "Glossário" },
  saviez_vous:      { fr: "Le Saviez-vous ?",     en: "Did You Know?",      es: "¿Sabías que…?",    pt: "Sabia que…?" },
  mythes:           { fr: "Mythes & Réalités",    en: "Myths & Reality",    es: "Mitos y Realidad", pt: "Mitos e Realidade" },
  science:          { fr: "Science du Vin",       en: "Wine Science",       es: "Ciencia del Vino", pt: "Ciência do Vinho" },
  comparaisons:     { fr: "Comparaisons",         en: "Comparisons",        es: "Comparaciones",    pt: "Comparações" },
};

const AtlasBlocksSection = ({ appellation, cepage, producer, lang = "fr" }) => {
  const [blocks, setBlocks] = useState(null); // null = loading, [] = empty, [...] = loaded
  const [openCat, setOpenCat] = useState(null); // category key currently expanded
  const [status, setStatus] = useState("cached"); // "cached" | "translating"
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // Determine entity + slug from whichever prop was provided.
  const { entityType, entitySlug, entityLabel, publicUrl } = useMemo(() => {
    if (appellation) {
      const s = slugify(appellation);
      return {
        entityType: "appellation",
        entitySlug: s,
        entityLabel: appellation,
        publicUrl: `https://app.geovinum.io/appellations/${s}`,
      };
    }
    if (cepage) {
      const s = slugify(cepage);
      return {
        entityType: "cepage",
        entitySlug: s,
        entityLabel: cepage,
        publicUrl: `https://app.geovinum.io/cepages/${s}`,
      };
    }
    if (producer) {
      const s = slugify(producer);
      return {
        entityType: "producer",
        entitySlug: s,
        entityLabel: producer,
        publicUrl: `https://app.geovinum.io/producers/${s}`,
      };
    }
    return {};
  }, [appellation, cepage, producer]);

  useEffect(() => {
    if (!entityType || !entitySlug) { setBlocks([]); return; }
    let cancelled = false;
    let timer = null;
    setBlocks(null);  // reset loading state when lang or entity changes
    setStatus("cached");

    const fetchOnce = async () => {
      const qs = new URLSearchParams({ lang });
      const { ok, data } = await apiFetch(`/api/public/atlas-blocks/${entityType}/${entitySlug}?${qs}`);
      if (cancelled) return;
      const list = ok && Array.isArray(data?.blocks) ? data.blocks : [];
      setBlocks(list);
      setStatus(data?.translation_status || "cached");
      setProgress({
        done: Number(data?.translated_count || 0),
        total: Number(data?.total_count || list.length || 0),
      });
      // If translation is in progress, poll every 8 s until it completes so the
      // content silently upgrades to the reader's language without a refresh.
      if (data?.translation_status === "translating") {
        timer = setTimeout(fetchOnce, 8000);
      }
      // Auto-expand the first non-empty category on the very first load.
      if (openCat == null) {
        const firstCat = ATLAS_CATEGORIES.find((c) => list.some((b) => b.category === c.key));
        if (firstCat) setOpenCat(firstCat.key);
      }
    };
    fetchOnce();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [entityType, entitySlug, lang]);

  // Group blocks by category (preserving ATLAS_CATEGORIES order).
  const groups = useMemo(() => {
    if (!Array.isArray(blocks)) return [];
    return ATLAS_CATEGORIES
      .map((c) => ({
        ...c,
        items: blocks
          .filter((b) => b.category === c.key)
          .sort((a, b) => (a.order ?? 999) - (b.order ?? 999)),
      }))
      .filter((g) => g.items.length > 0);
  }, [blocks]);

  // Loading / empty states — return null to fully collapse the section on
  // articles without an Atlas fiche (no visible artefact).
  if (blocks === null || blocks.length === 0) return null;

  const copy = COPY[lang] || COPY.fr;

  return (
    <section
      className="mt-14 pt-10 border-t border-[color:var(--gv-line)]"
      data-testid="article-atlas-blocks"
    >
      <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-3">
        {copy.eyebrow}
      </div>
      <h2 className="font-display text-2xl md:text-3xl text-[color:var(--gv-ink)] mb-2">
        {copy.title(entityLabel)}
      </h2>
      <p className="text-[13.5px] text-[color:var(--gv-ink)]/60 mb-6 flex items-center gap-3 flex-wrap">
        <span>{copy.subtitle(blocks.length)}</span>
        {status === "translating" && (
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[11.5px] font-medium"
            data-testid="atlas-translating-badge"
          >
            <Loader2 size={11} className="animate-spin" />
            {copy.translating(progress.done, progress.total || blocks.length)}
          </span>
        )}
      </p>

      <div className="space-y-2">
        {groups.map((g) => {
          const isOpen = openCat === g.key;
          const label = CATEGORY_LABEL_I18N[g.key]?.[lang] || g.label;
          return (
            <div
              key={g.key}
              className="border border-[color:var(--gv-line)] rounded-[5px] bg-white overflow-hidden"
              data-testid={`atlas-group-${g.key}`}
            >
              <button
                type="button"
                onClick={() => setOpenCat(isOpen ? null : g.key)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[color:var(--gv-mist)]/40 transition-colors"
                data-testid={`atlas-group-toggle-${g.key}`}
                aria-expanded={isOpen}
              >
                <span className="inline-flex items-center gap-2.5">
                  <span className="text-lg">{g.emoji}</span>
                  <span className="font-display text-[15.5px] text-[color:var(--gv-ink)]">{label}</span>
                  <span className="text-[11px] text-[color:var(--gv-ink)]/45 ml-1">{g.items.length}</span>
                </span>
                <ChevronDown
                  size={16}
                  className={`text-[color:var(--gv-ink)]/40 transition-transform ${isOpen ? "rotate-180" : ""}`}
                />
              </button>
              {isOpen && (
                <div className="px-4 pb-4 pt-1 space-y-4 border-t border-[color:var(--gv-line)] bg-[color:var(--gv-mist)]/20">
                  {g.items.map((b) => (
                    <article key={b.id} className="pt-3" data-testid={`atlas-block-${b.id}`}>
                      {b.title && (
                        <h3 className="font-display text-[16px] text-[color:var(--gv-ink)] mb-2">
                          {b.title}
                        </h3>
                      )}
                      <div
                        className="article-body text-[14.5px] leading-[1.7] text-[color:var(--gv-ink)]/85"
                        dangerouslySetInnerHTML={{ __html: renderMd(b.content || b.body || "") }}
                      />
                    </article>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <a
        href={publicUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-8 inline-flex items-center gap-2 px-5 py-3 rounded-[5px] bg-[color:var(--gv-pink)] text-white text-[14px] hover:bg-[color:var(--gv-pink-dark,#C01661)] transition-colors"
        data-testid="article-atlas-cta"
      >
        {copy.cta}
        <ExternalLink size={14} />
      </a>
    </section>
  );
};

export default AtlasBlocksSection;
